/** Headless chat state for the AI form-authoring assistant.
 *
 * Owns: turn list, in-flight job, busy flag, accept/reject per-turn.
 * Does NOT own: UI rendering, toasts, or the form definition itself. The
 * latest definition is read fresh from the caller on every `send()` so the
 * LLM always sees current state without us re-passing the whole form per
 * history turn (history is text-only — full JSON travels once per request).
 */
import { useCallback, useRef, useState } from 'react'
import {
  assistantApi, pollAssistantJob,
  type AssistantChatHistoryTurn, type AssistantChatResult, type AssistantJobRun,
} from '../api/assistant-api'
import { buildFormEnvelope, unwrapEnvelope, type AssistantFormEnvelopeArgs } from '../lib/form-envelope'
import type { FormDefinition } from '@/features/form-builder/api/form-builder-api'

export interface AssistantTurn {
  role: 'user' | 'assistant'
  content: string
  result?: AssistantChatResult
  accepted?: boolean | null
}

export interface UseAssistantChatArgs {
  /** Returns the latest editor envelope at send-time, NOT at hook-mount time. */
  getEnvelopeArgs: () => AssistantFormEnvelopeArgs
  /** Editor selection passed as context.selected_field_key. */
  getSelectedFieldKey: () => string | null
  /** Apply accepted form mutations back to the editor. */
  onApply: (next: FormDefinition) => void
  locale?: 'fa' | 'en'
  /** Surface errors (toast, logger, ...). */
  onError?: (message: string) => void
  /** Surface a success after accept. */
  onAccepted?: () => void
}

export interface UseAssistantChatReturn {
  turns: AssistantTurn[]
  job: AssistantJobRun | null
  busy: boolean
  send: (instruction: string) => Promise<void>
  accept: (turnIndex: number) => void
  reject: (turnIndex: number) => void
  reset: () => void
}

export function useAssistantChat({
  getEnvelopeArgs, getSelectedFieldKey, onApply, locale = 'fa', onError, onAccepted,
}: UseAssistantChatArgs): UseAssistantChatReturn {
  const [turns, setTurns] = useState<AssistantTurn[]>([])
  const [job, setJob] = useState<AssistantJobRun | null>(null)
  const [busy, setBusy] = useState(false)
  const turnsRef = useRef<AssistantTurn[]>([])
  turnsRef.current = turns

  const send = useCallback(async (instruction: string) => {
    const text = instruction.trim()
    if (!text || busy) return
    setBusy(true)
    const history: AssistantChatHistoryTurn[] = turnsRef.current.map((t) => ({
      role: t.role, content: t.content,
    }))
    setTurns((t) => [...t, { role: 'user', content: text }])

    try {
      const envelope = buildFormEnvelope(getEnvelopeArgs())
      const selectedFieldKey = getSelectedFieldKey()
      const { job_id } = await assistantApi.chat({
        form: envelope,
        instruction: text,
        locale,
        context: selectedFieldKey ? { selected_field_key: selectedFieldKey } : {},
        history,
      })
      const final = await pollAssistantJob(job_id, setJob)
      setJob(null)
      const result = final.result as AssistantChatResult | undefined
      const content = result?.assistant_message || final.error || '(پاسخی برنگشت)'
      setTurns((t) => [
        ...t,
        { role: 'assistant', content, result, accepted: result ? null : undefined },
      ])
    } catch (e) {
      const msg = (e as Error)?.message ?? String(e)
      onError?.(msg)
      setTurns((t) => [...t, { role: 'assistant', content: `خطا: ${msg}` }])
    } finally {
      setBusy(false)
    }
  }, [busy, getEnvelopeArgs, getSelectedFieldKey, locale, onError])

  const accept = useCallback((idx: number) => {
    const t = turnsRef.current[idx]
    if (!t?.result) return
    const next = unwrapEnvelope(t.result.updated_form)
    onApply(next)
    setTurns((arr) => arr.map((x, i) => (i === idx ? { ...x, accepted: true } : x)))
    onAccepted?.()
  }, [onApply, onAccepted])

  const reject = useCallback((idx: number) => {
    setTurns((arr) => arr.map((x, i) => (i === idx ? { ...x, accepted: false } : x)))
  }, [])

  const reset = useCallback(() => {
    setTurns([])
    setJob(null)
  }, [])

  return { turns, job, busy, send, accept, reject, reset }
}
